import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { emptySplitApi } from "./empty-split-api";
import {
  type QuickBookAdjustmentTypeInput,
  type QuickBookAdjustmentTypeMapping,
  type QuickBookClassMapping,
  type QuickBookClassMappingInput,
  type QuickBookClassMappingRule,
  type QuickBookClassMappingRuleInput,
  type QuickBookClassMappingsFilter,
  type QuickBookCompany,
  type QuickBookDefaultMapping,
  type QuickBookDefaultMappingInput,
  type QuickBookDefaultMappingsFilter,
  type QuickBookProduct,
  type QuickBookProductRelation,
  type QuickBookProductRelationFilter,
  type QuickBookProductsFilter,
  type QuickBooksConnectionHealth,
  QuickBooksDashboardMetrics,
  type QuickBooksSyncExclusionRule,
  type QuickBooksSyncExclusionRuleInput,
  type QuickBooksToken
} from "./types/quickbooks.type";

export const QUICKBOOKS_EXPENCE_COST_OF_GOODS_SOLD = "Cost of Goods Sold";

export const QUICKBOOKS_DEFAULT_MAPPINGS_QUERY =
  "{id,type,subtype,tier,quickbooks_company{id,company_name},quickbooks_product{id,quickbooks_product_id,quickbooks_product_name,quickbooks_product_type,quickbooks_product_description,is_active,quickbooks_income_account_id,quickbooks_income_account_name,quickbooks_expense_account_id,quickbooks_expense_account_name},quickbooks_income_account,quickbooks_expense_account}";

export const QUICKBOOKS_PRODUCTS_QUERY =
  "{id,quickbooks_company{id,company_name},quickbooks_product_id,quickbooks_product_name,quickbooks_product_type,quickbooks_product_description,is_active}";
export const QUICKBOOKS_ADJUSTMENT_TYPES_QUERY =
  "{id,adjustment_type,quickbooks_company{id,company_name},quickbooks_product{id,quickbooks_product_id,quickbooks_product_name,quickbooks_product_type,quickbooks_product_description,is_active}}";
export const QUICKBOOKS_DEFAULT_LIMIT = 300;

const quickBooksApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    //tokens
    getQuickBooksTokens: builder.query<QuickBooksToken[], void>({
      query: () => "/quickbooks/tokens/",
      providesTags: ["QuickBooksTokens"]
    }),
    // Drives the reconnect banner. Tagged with QuickBooksTokens so re-authorizing (which
    // invalidates that tag) re-reads health and clears the banner without a page reload.
    getQuickBooksConnectionHealth: builder.query<
      QuickBooksConnectionHealth,
      void
    >({
      query: () => "/quickbooks/tokens/health/",
      providesTags: ["QuickBooksTokens"]
    }),
    updateQuickBooksToken: builder.mutation<QuickBooksToken, QuickBooksToken>({
      query: ({ id, ...data }) => ({
        url: `/quickbooks/tokens/${id}/`,
        method: "PATCH",
        body: data
      }),
      invalidatesTags: ["QuickBooksTokens"]
    }),
    deleteQuickBooksToken: builder.mutation<void, number>({
      query: (id) => ({
        url: `/quickbooks/tokens/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["QuickBooksTokens"]
    }),
    //companies
    getQuickBooksCompanies: builder.query<QuickBookCompany[], void>({
      query: () => "/quickbooks/companies/",
      providesTags: ["QuickBooksCompanies"]
    }),
    //products
    getQuickBooksProducts: builder.query<
      QuickBookProduct[],
      QuickBookProductsFilter
    >({
      query: ({ query = QUICKBOOKS_PRODUCTS_QUERY, search, ...rest }) =>
        `/quickbooks/products/?query=${query}&${formatValidQueryParamUrlCommaSeparated(
          { ...rest, search: search && encodeURIComponent(search) }
        )}`,
      transformResponse: (response: { results: QuickBookProduct[] }) => {
        return response?.results || [];
      },
      providesTags: ["QuickBooksProducts"]
    }),
    syncQuickBooksProducts: builder.mutation<
      string,
      { quickbooks_realm_id: string }
    >({
      query: ({ quickbooks_realm_id }) => ({
        url: `/quickbooks/products/fetch/`,
        body: {
          quickbooks_realm_id
        },
        method: "POST"
      }),
      invalidatesTags: ["QuickBooksProducts"]
    }),
    //default mappings
    bulkAddQuickBooksDefaultMappings: builder.mutation<
      void,
      QuickBookDefaultMappingInput[]
    >({
      query: (body: QuickBookDefaultMappingInput[]) => ({
        url: `/quickbooks/default-mappings/bulk-add/`,
        body,
        method: "POST"
      }),
      invalidatesTags: ["QuickBooksDefaultMappings"]
    }),
    updateQuickBooksDefaultMapping: builder.mutation<
      void,
      QuickBookDefaultMappingInput
    >({
      query: ({ id, ...body }: QuickBookDefaultMappingInput) => ({
        url: `/quickbooks/default-mappings/${id}/`,
        body,
        method: "PATCH"
      }),
      invalidatesTags: ["QuickBooksDefaultMappings"]
    }),
    bulkDeleteQuickBooksDefaultMapping: builder.mutation<
      void,
      { ids: number[] }
    >({
      query: (ids) => ({
        url: `/quickbooks/default-mappings/bulk-delete/`,
        body: ids,
        method: "DELETE"
      }),
      invalidatesTags: ["QuickBooksDefaultMappings"]
    }),
    getQuickBooksDefaultMappings: builder.query<
      QuickBookDefaultMapping[],
      QuickBookDefaultMappingsFilter
    >({
      query: ({ query = QUICKBOOKS_DEFAULT_MAPPINGS_QUERY, ...filter }) =>
        `/quickbooks/default-mappings/${formatValidQueryParamUrlCommaSeparated(
          { query, ...filter },
          true
        )}`,
      providesTags: ["QuickBooksDefaultMappings"]
    }),
    //adjustment types
    getQuickBooksAdjustmentTypesMappings: builder.query<
      QuickBookAdjustmentTypeMapping[],
      QuickBookDefaultMappingsFilter
    >({
      query: ({ query = QUICKBOOKS_ADJUSTMENT_TYPES_QUERY, ...filter }) =>
        `/quickbooks/adjustment-mappings/${formatValidQueryParamUrlCommaSeparated(
          { query, ...filter },
          true
        )}`,
      providesTags: ["QuickBooksAdjustmentTypesMappings"]
    }),
    bulkAddQuickBooksAdjustmentTypesMappings: builder.mutation<
      void,
      QuickBookAdjustmentTypeInput[]
    >({
      query: (body: QuickBookAdjustmentTypeInput[]) => ({
        url: `/quickbooks/adjustment-mappings/bulk-add/`,
        body,
        method: "POST"
      }),
      invalidatesTags: ["QuickBooksAdjustmentTypesMappings"]
    }),
    updateQuickBooksAdjustmentTypesMapping: builder.mutation<
      void,
      QuickBookAdjustmentTypeInput
    >({
      query: ({ id, ...body }: QuickBookAdjustmentTypeInput) => ({
        url: `/quickbooks/adjustment-mappings/${id}/`,
        body,
        method: "PATCH"
      }),
      invalidatesTags: ["QuickBooksAdjustmentTypesMappings"]
    }),
    bulkDeleteQuickBooksAdjustmentTypesMapping: builder.mutation<
      void,
      { ids: number[] }
    >({
      query: (ids) => ({
        url: `/quickbooks/adjustment-mappings/bulk-delete/`,
        body: ids,
        method: "DELETE"
      }),
      invalidatesTags: ["QuickBooksAdjustmentTypesMappings"]
    }),
    //product relations
    getQuickBooksProductsRelations: builder.query<
      QuickBookProductRelation[],
      QuickBookProductRelationFilter
    >({
      query: (filter) =>
        `/quickbooks/products/relations/${formatValidQueryParamUrlCommaSeparated(
          filter,
          true
        )}`,
      transformResponse: (response: {
        results: QuickBookProductRelation[];
      }) => {
        return response?.results || [];
      },
      providesTags: ["QuickBooksProductsRelations"]
    }),
    getQuickBooksProductsRelationsCount: builder.query<
      number,
      QuickBookProductRelationFilter
    >({
      query: (filter) =>
        `/quickbooks/products/relations/${formatValidQueryParamUrlCommaSeparated(
          { ...filter, limit: 0, offset: 0 },
          true
        )}`,
      transformResponse: (response: { count: number }) => {
        return response?.count || 0;
      },
      providesTags: ["QuickBooksProductsRelations"]
    }),
    bulkAddQuickBooksProductsRelations: builder.mutation<
      string,
      QuickBookProductRelation[]
    >({
      query: (body) => ({
        url: `/quickbooks/products/bulk-map/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["QuickBooksProductsRelations", "Products"]
    }),
    getRefundAccount: builder.query<any, number>({
      query: (companyId) =>
        `/quickbooks/companies/${companyId}/refund-account/`,
      providesTags: ["QuickBooksRefundAccounts"]
    }),
    setRefundAccount: builder.mutation<
      any,
      { companyId: number; accountId: number }
    >({
      query: ({ companyId, accountId }) => ({
        url: `/quickbooks/companies/${companyId}/refund-account/`,
        method: "POST",
        body: { account_id: accountId }
      }),
      invalidatesTags: ["QuickBooksRefundAccounts"]
    }),
    getTaxProduct: builder.query<QuickBookProduct | null, number>({
      query: (companyId) => `/quickbooks/companies/${companyId}/tax-product/`,
      providesTags: ["QuickBooksTaxProducts"]
    }),
    setTaxProduct: builder.mutation<
      any,
      { companyId: number; productId: number | null }
    >({
      query: ({ companyId, productId }) => ({
        url: `/quickbooks/companies/${companyId}/tax-product/`,
        method: "POST",
        body: { product_id: productId }
      }),
      invalidatesTags: ["QuickBooksTaxProducts"]
    }),
    syncQuickBooksAccounts: builder.mutation<
      {
        total: number;
        created: number;
        updated: number;
        deleted: number;
      },
      void
    >({
      query: () => ({
        url: `/practices/sync-quickbooks-accounts/`,
        method: "POST"
      }),
      invalidatesTags: ["QuickBooksAccounts"]
    }),
    getQuickBooksClassMappings: builder.query<
      QuickBookClassMapping[],
      QuickBookClassMappingsFilter
    >({
      query: (filter) =>
        `/quickbooks/class-mappings/${formatValidQueryParamUrlCommaSeparated(
          { ...filter, limit: 100 },
          true
        )}`,
      providesTags: ["QuickBooksClassMappings"]
    }),
    bulkCreateQuickBooksClassMappings: builder.mutation<
      QuickBookClassMapping[],
      { items: QuickBookClassMappingInput[] }
    >({
      query: (body) => ({
        url: `/quickbooks/class-mappings/bulk-create/`,
        body,
        method: "POST"
      }),
      invalidatesTags: ["QuickBooksClassMappings"]
    }),
    bulkUpdateQuickBooksClassMappings: builder.mutation<
      QuickBookClassMapping[],
      { items: QuickBookClassMappingInput[] }
    >({
      query: (body) => ({
        url: `/quickbooks/class-mappings/bulk-update/`,
        body,
        method: "PATCH"
      }),
      invalidatesTags: ["QuickBooksClassMappings"]
    }),
    updateQuickBooksClassMapping: builder.mutation<
      void,
      QuickBookClassMappingInput
    >({
      query: ({ id, ...body }: QuickBookClassMappingInput) => ({
        url: `/quickbooks/class-mappings/${id}/`,
        body,
        method: "PATCH"
      }),
      invalidatesTags: ["QuickBooksClassMappings"]
    }),
    bulkDeleteQuickBooksClassMapping: builder.mutation<void, { ids: number[] }>(
      {
        query: (body) => ({
          url: `/quickbooks/class-mappings/bulk-delete/`,
          body,
          method: "DELETE"
        }),
        invalidatesTags: ["QuickBooksClassMappings"]
      }
    ),
    getQuickBooksClassMappingRules: builder.query<
      QuickBookClassMappingRule[],
      QuickBookClassMappingsFilter
    >({
      query: (filter) =>
        `/quickbooks/class-mapping-rules/${formatValidQueryParamUrlCommaSeparated(
          { ...filter, limit: 100 },
          true
        )}`,
      providesTags: ["QuickBooksClassMappingRules"]
    }),
    bulkCreateQuickBooksClassMappingRules: builder.mutation<
      QuickBookClassMappingRule[],
      { items: QuickBookClassMappingRuleInput[] }
    >({
      query: (body) => ({
        url: `/quickbooks/class-mapping-rules/bulk-create/`,
        body,
        method: "POST"
      }),
      invalidatesTags: ["QuickBooksClassMappingRules"]
    }),
    bulkUpdateQuickBooksClassMappingRules: builder.mutation<
      QuickBookClassMappingRule[],
      { items: QuickBookClassMappingRuleInput[] }
    >({
      query: (body) => ({
        url: `/quickbooks/class-mapping-rules/bulk-update/`,
        body,
        method: "PATCH"
      }),
      invalidatesTags: ["QuickBooksClassMappingRules"]
    }),
    bulkDeleteQuickBooksClassMappingRule: builder.mutation<
      void,
      { ids: number[] }
    >({
      query: (body) => ({
        url: `/quickbooks/class-mapping-rules/bulk-delete/`,
        body,
        method: "DELETE"
      }),
      invalidatesTags: ["QuickBooksClassMappingRules"]
    }),
    getQuickBooksSyncExclusionRules: builder.query<
      QuickBooksSyncExclusionRule[],
      { quickbooks_company: number }
    >({
      query: ({ quickbooks_company }) =>
        `/quickbooks/sync-exclusion-rules/?quickbooks_company=${quickbooks_company}&limit=100`,
      providesTags: ["QuickBooksSyncExclusionRules"]
    }),
    createQuickBooksSyncExclusionRule: builder.mutation<
      QuickBooksSyncExclusionRule,
      QuickBooksSyncExclusionRuleInput
    >({
      query: (body) => ({
        url: `/quickbooks/sync-exclusion-rules/`,
        body,
        method: "POST"
      }),
      // A rule changes which sales the backend reports as excluded, so the sale
      // lists that carry `qbo_sync_excluded` and the Sync Exceptions report,
      // which omits excluded sales, must re-read.
      invalidatesTags: [
        "QuickBooksSyncExclusionRules",
        "QuickBooksSyncExceptions",
        "Sales",
        "Paginated-Sales"
      ]
    }),
    deleteQuickBooksSyncExclusionRule: builder.mutation<void, number>({
      query: (id) => ({
        url: `/quickbooks/sync-exclusion-rules/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: [
        "QuickBooksSyncExclusionRules",
        "QuickBooksSyncExceptions",
        "Sales",
        "Paginated-Sales"
      ]
    }),
    getQuickBooksDashboard: builder.query<
      QuickBooksDashboardMetrics,
      {
        company_id: number;
        start_date?: string;
        end_date?: string;
      }
    >({
      query: ({ company_id, start_date, end_date }) => {
        const params = new URLSearchParams();
        params.append("company_id", String(company_id));
        if (start_date) params.append("start_date", start_date);
        if (end_date) params.append("end_date", end_date);
        return `/quickbooks-dashboard/?${params.toString()}`;
      },
      providesTags: ["QuickBooksDashboard"]
    })
  })
});

export const {
  useGetQuickBooksTokensQuery,
  useUpdateQuickBooksTokenMutation,
  useDeleteQuickBooksTokenMutation,
  useGetQuickBooksCompaniesQuery,
  useGetQuickBooksProductsQuery,
  useSyncQuickBooksProductsMutation,
  useBulkAddQuickBooksProductsRelationsMutation,
  useGetQuickBooksProductsRelationsQuery,
  useGetQuickBooksProductsRelationsCountQuery,
  useBulkAddQuickBooksDefaultMappingsMutation,
  useUpdateQuickBooksDefaultMappingMutation,
  useBulkDeleteQuickBooksDefaultMappingMutation,
  useGetQuickBooksDefaultMappingsQuery,
  useGetQuickBooksAdjustmentTypesMappingsQuery,
  useBulkAddQuickBooksAdjustmentTypesMappingsMutation,
  useUpdateQuickBooksAdjustmentTypesMappingMutation,
  useBulkDeleteQuickBooksAdjustmentTypesMappingMutation,
  useGetRefundAccountQuery,
  useLazyGetRefundAccountQuery,
  useSetRefundAccountMutation,
  useGetTaxProductQuery,
  useLazyGetTaxProductQuery,
  useSetTaxProductMutation,
  useSyncQuickBooksAccountsMutation,
  useGetQuickBooksClassMappingsQuery,
  useBulkCreateQuickBooksClassMappingsMutation,
  useBulkUpdateQuickBooksClassMappingsMutation,
  useUpdateQuickBooksClassMappingMutation,
  useBulkDeleteQuickBooksClassMappingMutation,
  useGetQuickBooksClassMappingRulesQuery,
  useBulkCreateQuickBooksClassMappingRulesMutation,
  useBulkUpdateQuickBooksClassMappingRulesMutation,
  useBulkDeleteQuickBooksClassMappingRuleMutation,
  useGetQuickBooksSyncExclusionRulesQuery,
  useCreateQuickBooksSyncExclusionRuleMutation,
  useDeleteQuickBooksSyncExclusionRuleMutation,
  useGetQuickBooksDashboardQuery,
  useGetQuickBooksConnectionHealthQuery
} = quickBooksApi;
